import React from 'react';
import { withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg/geography';
import { GuideLink } from '@components/common/next-link';
import { useI18NextContext } from '@components/hooks';
import { TripTypes, Page } from '@model/common';
import { TravelGuide } from '@model/content';
import { SimpleTile } from '@components/common/simple-tile';
import { Carousel } from '@components/common';

/* ***************** *
 *       Types       *
 * ***************** */

export interface TravelGuideCarouselProps {
  path?: string;
  tripType?: TripTypes;
  guides: Array<TravelGuide>;
  destination?: string;
  testId?: string;
  className?: string;
}

const MAX_NUMBER_OF_TILES = 3;

/* ******************************* *
 *    TravelGuideCarouselComponent    *
 * ******************************* */

export const TravelGuideCarouselComponent = ({
  path,
  guides,
  tripType,
  destination,
  className,
  testId
}: TravelGuideCarouselProps) => {
  const t: any = useI18NextContext();

  if (!path || !guides || !guides.length) return null;

  /* *** RENDERERS *** */
  const renderTravelGuideTiles = () =>
    guides.slice(0, MAX_NUMBER_OF_TILES).map((travelGuide) => {
      const { title, shortDescription, hero, targetPageConfigPath } = travelGuide;

      return (
        <GuideLink
          key={path}
          path={path}
          guidePath={targetPageConfigPath}
          tripType={tripType}
          guidePage={Page.TRAVEL_GUIDES}
        >
          <SimpleTile title={title} shortDescription={shortDescription} imageUrl={hero.url} sizes={'200px'} />
        </GuideLink>
      );
    });

  const builder: DestinationLinkBuilder = new DestinationLinkBuilder();
  const travelGuideLink: LinkItem = builder.getGuidesLink(path, Page.TRAVEL_GUIDES, tripType);

  const link =
    guides.length > MAX_NUMBER_OF_TILES
      ? { label: t('common__label--view-more-articles'), linkItem: travelGuideLink }
      : undefined;

  return (
    <Carousel
      className={className}
      testId={testId || TestId.guides.groupComponent}
      title={t('travel-guides__page-title--label', { destination })}
      link={link}
      slidesToShow={MAX_NUMBER_OF_TILES}
    >
      {renderTravelGuideTiles()}
    </Carousel>
  );
};

export const TravelGuideCarousel = withTheme(TravelGuideCarouselComponent);
